import {
  DateScopedCompletion,
  DateScopedListData,
  List,
  ListStatus,
  OptimizedCompletionStorage,
  CompletionArchive,
  CompletionStorageStats
} from '../types/lists';
import { dataBackupService } from './dataBackupService';

export class UniversalCompletionService {
  private completions = new Map<string, DateScopedCompletion>();
  private cache = new Map<string, { data: DateScopedListData; timestamp: number }>();
  private readonly STORAGE_KEY = 'godmode_universal_completions';
  private readonly ARCHIVE_KEY = 'godmode_completion_archives_index';
  private readonly SETTINGS_KEY = 'godmode_completion_settings';
  private readonly CACHE_TTL = 60000; // 1 minute
  private readonly DEFAULT_ARCHIVE_THRESHOLD_DAYS = 30;

  constructor() {
    this.loadCompletions();
  }

  // Get archive threshold from settings or use default
  private getArchiveThresholdDays(): number {
    try {
      const settings = localStorage.getItem(this.SETTINGS_KEY);
      if (settings) {
        const parsed = JSON.parse(settings);
        return parsed.archiveThresholdDays || this.DEFAULT_ARCHIVE_THRESHOLD_DAYS;
      }
    } catch (e) {
      console.warn('Failed to load completion settings:', e);
    }
    return this.DEFAULT_ARCHIVE_THRESHOLD_DAYS;
  }

  // Set archive threshold in settings
  setArchiveThresholdDays(days: number): void {
    try {
      const settings = localStorage.getItem(this.SETTINGS_KEY);
      const parsed = settings ? JSON.parse(settings) : {};
      parsed.archiveThresholdDays = days;
      localStorage.setItem(this.SETTINGS_KEY, JSON.stringify(parsed));
    } catch (e) {
      console.error('Failed to save completion settings:', e);
    }
  }

  // Migrate from v1 to v2 format
  private migrateV1ToV2(v1Data: Record<string, DateScopedCompletion>): OptimizedCompletionStorage {
    const v2: OptimizedCompletionStorage = {
      v: 2,
      archiveThresholdDays: this.getArchiveThresholdDays(),
      lastArchiveDate: null,
      l: {}
    };

    // Group by listId, removing time component from keys
    Object.entries(v1Data).forEach(([_key, value]) => {
      const listId = value.listId;
      const date = value.contextDate; // YYYY-MM-DD

      if (!v2.l[listId]) {
        v2.l[listId] = {
          d: {},
          u: typeof value.updatedAt === 'string' ? value.updatedAt : value.updatedAt.toISOString()
        };
      }

      // Merge completions for same date (in case of multiple time entries)
      const existingIds = v2.l[listId].d[date] || [];
      const newIds = value.completedItemIds || [];
      const mergedIds = [...new Set([...existingIds, ...newIds])];
      v2.l[listId].d[date] = mergedIds;

      const updatedAt = typeof value.updatedAt === 'string' ? value.updatedAt : value.updatedAt.toISOString();
      if (updatedAt > v2.l[listId].u) {
        v2.l[listId].u = updatedAt;
      }
    });

    return v2;
  }

  // Load completions with version detection and migration
  private loadCompletions(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return;

      const parsed = JSON.parse(stored);

      // Check version
      if (parsed.v === 2) {
        this.loadV2Format(parsed);
      } else {
        // V1 format (original) - migrate
        console.log('📋 Migrating completion data from v1 to v2 format...');
        const v2Data = this.migrateV1ToV2(parsed);
        this.loadV2Format(v2Data);
        this.saveCompletions(); // Save in new format
        console.log('📋 Migration complete');
      }

      // Check if archiving is needed
      this.checkAndArchiveIfNeeded();

    } catch (error) {
      console.error('Error loading completions:', error);
      this.completions.clear();
    }
  }

  private loadV2Format(data: OptimizedCompletionStorage): void {
    this.completions.clear();

    Object.entries(data.l).forEach(([listId, listData]) => {
      Object.entries(listData.d).forEach(([date, completedItemIds]) => {
        const key = `${listId}@${date}`;

        this.completions.set(key, {
          listId,
          contextDate: date,
          completedItemIds: completedItemIds as string[],
          createdAt: new Date(listData.u),
          updatedAt: new Date(listData.u)
        });
      });
    });

    console.log(`📋 Loaded ${this.completions.size} completion records (v2 format)`);
  }

  // Check if we need to archive old entries
  private async checkAndArchiveIfNeeded(): Promise<void> {
    const thresholdDays = this.getArchiveThresholdDays();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - thresholdDays);
    const cutoffStr = cutoffDate.toISOString().split('T')[0];

    // Collect entries to archive
    let archivableCount = 0;

    for (const completion of this.completions.values()) {
      if (completion.contextDate < cutoffStr) {
        archivableCount++;
      }
    }

    if (archivableCount > 0) {
      console.log(`📦 ${archivableCount} entries eligible for archiving (older than ${thresholdDays} days)`);

      // Dispatch event to notify UI that archiving is available
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('completion-archive-available', {
          detail: { count: archivableCount, oldestDate: cutoffStr }
        }));
      }
    }
  }

  // Archive old entries to the user's configured backup provider
  async archiveOldEntries(): Promise<{ success: boolean; archivedCount: number; error?: string }> {
    const thresholdDays = this.getArchiveThresholdDays();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - thresholdDays);
    const cutoffStr = cutoffDate.toISOString().split('T')[0];

    // Collect entries to archive
    const toArchive: Map<string, DateScopedCompletion> = new Map();
    let oldestDate = '';
    let newestDate = '';

    for (const [key, completion] of this.completions.entries()) {
      if (completion.contextDate < cutoffStr) {
        toArchive.set(key, completion);
        if (!oldestDate || completion.contextDate < oldestDate) {
          oldestDate = completion.contextDate;
        }
        if (!newestDate || completion.contextDate > newestDate) {
          newestDate = completion.contextDate;
        }
      }
    }

    if (toArchive.size === 0) {
      return { success: true, archivedCount: 0 };
    }

    // Build archive object
    const archive: CompletionArchive = {
      v: 1,
      archivedAt: new Date().toISOString(),
      dateRange: { start: oldestDate, end: newestDate },
      data: {}
    };

    for (const [_, completion] of toArchive.entries()) {
      if (!archive.data[completion.listId]) {
        archive.data[completion.listId] = { d: {}, u: completion.updatedAt.toISOString() };
      }
      archive.data[completion.listId].d[completion.contextDate] = completion.completedItemIds;
    }

    try {
      // Save archive using the backup service
      const archiveFilename = `godmode-completions-archive-${oldestDate}-to-${newestDate}.json`;

      // Try to save to configured backup provider
      const providers = dataBackupService.getAvailableProviders();
      const configuredProvider = providers.find(p => p.isConfigured && p.supportsAutoBackup);

      if (configuredProvider?.id === 'google_drive') {
        // Archive to Google Drive
        const { simplifiedDriveService } = await import('./googleDriveSimplified');
        await simplifiedDriveService.uploadBackupFile(archiveFilename, JSON.stringify(archive, null, 2));
        console.log(`📦 Archived ${toArchive.size} entries to Google Drive`);
      } else {
        // Fall back to local storage of archive index + prompt user to download
        await this.saveArchiveLocally(archive, archiveFilename);
        console.log(`📦 Archived ${toArchive.size} entries locally`);
      }

      // Update archive index
      this.updateArchiveIndex(archiveFilename, oldestDate, newestDate, toArchive.size);

      // Remove archived entries from active storage
      for (const key of toArchive.keys()) {
        this.completions.delete(key);
        this.cache.delete(key);
      }

      this.saveCompletions();

      return { success: true, archivedCount: toArchive.size };

    } catch (error) {
      console.error('Failed to archive completions:', error);
      return {
        success: false,
        archivedCount: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // Save archive locally and prompt for download
  private async saveArchiveLocally(archive: CompletionArchive, filename: string): Promise<void> {
    // Trigger download
    const blob = new Blob([JSON.stringify(archive, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Maintain index of archived data
  private updateArchiveIndex(filename: string, startDate: string, endDate: string, count: number): void {
    const indexStr = localStorage.getItem(this.ARCHIVE_KEY);
    const index = indexStr ? JSON.parse(indexStr) : { archives: [] };

    index.archives.push({
      filename,
      dateRange: { start: startDate, end: endDate },
      entriesCount: count,
      archivedAt: new Date().toISOString()
    });

    localStorage.setItem(this.ARCHIVE_KEY, JSON.stringify(index));
  }

  // Get list of archived completion data
  getArchiveIndex(): Array<{
    filename: string;
    dateRange: { start: string; end: string };
    entriesCount: number;
    archivedAt: string;
  }> {
    const indexStr = localStorage.getItem(this.ARCHIVE_KEY);
    if (!indexStr) return [];
    try {
      const index = JSON.parse(indexStr);
      return index.archives || [];
    } catch {
      return [];
    }
  }

  // Load archived data (for reports, etc.)
  async loadArchivedData(filename: string): Promise<CompletionArchive | null> {
    try {
      const providers = dataBackupService.getAvailableProviders();
      const configuredProvider = providers.find(p => p.isConfigured && p.supportsAutoBackup);

      if (configuredProvider?.id === 'google_drive') {
        const { simplifiedDriveService } = await import('./googleDriveSimplified');
        const files = await simplifiedDriveService.listBackupFiles();
        const archiveFile = files.find(f => f.name === filename);

        if (archiveFile) {
          const content = await simplifiedDriveService.downloadBackupFile(archiveFile.id);
          return JSON.parse(content);
        }
      }

      // For local archives, user would need to upload the file
      return null;
    } catch (error) {
      console.error('Failed to load archived data:', error);
      return null;
    }
  }

  // Save in optimized v2 format
  private saveCompletions(): void {
    try {
      const v2: OptimizedCompletionStorage = {
        v: 2,
        archiveThresholdDays: this.getArchiveThresholdDays(),
        lastArchiveDate: null,
        l: {}
      };

      for (const [_key, completion] of this.completions.entries()) {
        const listId = completion.listId;
        const date = completion.contextDate;

        if (!v2.l[listId]) {
          v2.l[listId] = { d: {}, u: completion.updatedAt.toISOString() };
        }

        v2.l[listId].d[date] = completion.completedItemIds;

        if (completion.updatedAt.toISOString() > v2.l[listId].u) {
          v2.l[listId].u = completion.updatedAt.toISOString();
        }
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(v2));

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('universal-completion-updated'));
      }
    } catch (error) {
      console.error('Error saving completions:', error);
    }
  }

  // Get storage statistics
  getStorageStats(): CompletionStorageStats {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    const entriesByList: Record<string, number> = {};
    let oldestEntry: string | null = null;
    let newestEntry: string | null = null;

    const thresholdDays = this.getArchiveThresholdDays();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - thresholdDays);
    const cutoffStr = cutoffDate.toISOString().split('T')[0];
    let archivableCount = 0;

    for (const completion of this.completions.values()) {
      entriesByList[completion.listId] = (entriesByList[completion.listId] || 0) + 1;

      if (!oldestEntry || completion.contextDate < oldestEntry) {
        oldestEntry = completion.contextDate;
      }
      if (!newestEntry || completion.contextDate > newestEntry) {
        newestEntry = completion.contextDate;
      }
      if (completion.contextDate < cutoffStr) {
        archivableCount++;
      }
    }

    const archives = this.getArchiveIndex();

    return {
      totalEntries: this.completions.size,
      totalBytes: stored ? new Blob([stored]).size : 0,
      entriesByList,
      oldestEntry,
      newestEntry,
      archivableCount,
      archivedFilesCount: archives.length
    };
  }

  // Main API - get list data for any context date
  getListDataForContext(list: List, contextDate: Date): DateScopedListData {
    const cacheKey = this.getContextKey(list.id, contextDate);
    const cached = this.cache.get(cacheKey);

    console.log(`🔍 Universal Completion: Getting list data for context`, {
      listId: list.id,
      listTitle: list.title,
      contextDate: contextDate.toISOString(),
      contextDateString: contextDate.toDateString(),
      key: cacheKey,
      hasCached: !!cached,
      allKeys: Array.from(this.completions.keys())
    });

    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      console.log(`📋 Universal Completion: Using cached data for ${cacheKey}:`, cached.data.progress + '%');
      return cached.data;
    }

    const data = this.computeListDataForContext(list, contextDate);
    this.cache.set(cacheKey, { data, timestamp: Date.now() });
    console.log(`📋 Universal Completion: Computed fresh data for ${cacheKey}:`, data.progress + '%', `(${data.completedItems}/${data.totalItems})`);
    return data;
  }

  // Update item completion for specific context
  updateItemCompletion(listId: string, itemId: string, completed: boolean, contextDate: Date): void {
    const key = this.getContextKey(listId, contextDate);
    const existing = this.completions.get(key) || this.createEmptyCompletion(listId, contextDate);

    console.log(`🔍 Universal Completion: Updating item completion`, {
      listId,
      itemId,
      completed,
      contextDate: contextDate.toISOString(),
      contextDateString: contextDate.toDateString(),
      key
    });

    const completedIds = new Set(existing.completedItemIds);
    if (completed) {
      completedIds.add(itemId);
      console.log(`✅ Universal Completion: Marked item ${itemId} as completed for ${key}`);
    } else {
      completedIds.delete(itemId);
      console.log(`🔄 Universal Completion: Marked item ${itemId} as incomplete for ${key}`);
    }

    this.completions.set(key, {
      ...existing,
      completedItemIds: Array.from(completedIds),
      updatedAt: new Date()
    });

    this.cache.delete(key); // Invalidate cache
    this.saveCompletions();
  }

  // Get all completion records for a list
  getListCompletionHistory(listId: string): DateScopedCompletion[] {
    return Array.from(this.completions.values())
      .filter(completion => completion.listId === listId)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  // Get completion summary across dates
  getListCompletionSummary(listId: string): {
    totalDaysWorked: number;
    lastWorkedDate: string | null;
    averageCompletionRate: number;
  } {
    const history = this.getListCompletionHistory(listId);

    if (history.length === 0) {
      return {
        totalDaysWorked: 0,
        lastWorkedDate: null,
        averageCompletionRate: 0
      };
    }

    const totalDaysWorked = history.length;
    const lastWorkedDate = history[0].contextDate;

    // Calculate average completion rate (would need list data to be accurate)
    const averageCompletionRate = 0; // Placeholder - would need more context

    return {
      totalDaysWorked,
      lastWorkedDate,
      averageCompletionRate
    };
  }

  // Bulk update multiple items for a context
  bulkUpdateItemCompletion(
    listId: string,
    itemUpdates: { itemId: string; completed: boolean }[],
    contextDate: Date
  ): void {
    const key = this.getContextKey(listId, contextDate);
    const existing = this.completions.get(key) || this.createEmptyCompletion(listId, contextDate);

    const completedIds = new Set(existing.completedItemIds);

    itemUpdates.forEach(({ itemId, completed }) => {
      if (completed) {
        completedIds.add(itemId);
      } else {
        completedIds.delete(itemId);
      }
    });

    this.completions.set(key, {
      ...existing,
      completedItemIds: Array.from(completedIds),
      updatedAt: new Date()
    });

    this.cache.delete(key); // Invalidate cache
    this.saveCompletions();

    console.log(`🔄 Universal Completion: Bulk updated ${itemUpdates.length} items for ${key}`);
  }

  // Clear completion data for a specific context
  clearContextCompletion(listId: string, contextDate: Date): void {
    const key = this.getContextKey(listId, contextDate);
    this.completions.delete(key);
    this.cache.delete(key);
    this.saveCompletions();

    console.log(`🧹 Universal Completion: Cleared completion data for ${key}`);
  }

  // Clear all completion data for a list
  clearListCompletion(listId: string): void {
    const keysToDelete = Array.from(this.completions.keys())
      .filter(key => key.startsWith(`${listId}@`));

    keysToDelete.forEach(key => {
      this.completions.delete(key);
      this.cache.delete(key);
    });

    this.saveCompletions();
    console.log(`🧹 Universal Completion: Cleared all completion data for list ${listId}`);
  }

  // Get context key for storage/lookup - NOW USES DATE-ONLY FORMAT (v2)
  private getContextKey(listId: string, contextDate: Date): string {
    const dateStr = contextDate.toISOString().split('T')[0];
    return `${listId}@${dateStr}`;
  }


  // Compute list data for a specific context
  private computeListDataForContext(list: List, contextDate: Date): DateScopedListData {
    // 1. If list is permanently marked complete, return 100% progress
    if (list.status === 'complete') {
      const itemsWithCompletion = list.items.map(item => ({
        ...item,
        completed: true,
        completedAt: list.updatedAt
      }));

      return {
        listId: list.id,
        contextDate: contextDate.toISOString().split('T')[0],
        baseList: list,
        items: itemsWithCompletion,
        progress: 100,
        completedItems: list.items.length,
        totalItems: list.items.length,
        status: 'complete',
        isCompletelyFinished: true
      };
    }

    // 2. Get completion for this SPECIFIC context date
    // Each calendar event occurrence should show its own completion state
    // regardless of whether the list is recurring or one-time
    const key = this.getContextKey(list.id, contextDate);
    const completion = this.completions.get(key) || null;

    const completedIds = new Set(completion?.completedItemIds || []);

    const itemsWithCompletion = list.items.map(item => ({
      ...item,
      completed: completedIds.has(item.id),
      completedAt: completedIds.has(item.id) ? completion?.updatedAt : undefined
    }));

    const completedCount = itemsWithCompletion.filter(item => item.completed).length;

    const progress = list.items.length === 0 ? 100 : Math.round((completedCount / list.items.length) * 100);

    let contextualStatus: ListStatus = list.status;
    if (progress === 100) contextualStatus = 'complete';
    else if (progress > 0 && list.status === 'ready') contextualStatus = 'in_progress';

    return {
      listId: list.id,
      contextDate: contextDate.toISOString().split('T')[0],
      baseList: list,
      items: itemsWithCompletion,
      progress,
      completedItems: completedCount,
      totalItems: list.items.length,
      status: contextualStatus,
      isCompletelyFinished: progress === 100
    };
  }

  // Create empty completion record
  private createEmptyCompletion(listId: string, contextDate: Date): DateScopedCompletion {
    return {
      listId,
      contextDate: contextDate.toISOString().split('T')[0],
      completedItemIds: [],
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  // Get all unique dates where a list has completion data
  getListWorkingDates(listId: string): string[] {
    return Array.from(this.completions.values())
      .filter(completion => completion.listId === listId)
      .map(completion => completion.contextDate)
      .sort()
      .filter((date, index, array) => array.indexOf(date) === index); // Remove duplicates
  }

  // Check if a list has any completion data
  hasCompletionData(listId: string): boolean {
    return Array.from(this.completions.values())
      .some(completion => completion.listId === listId);
  }

  // Get completion rate for a specific date
  getCompletionRate(listId: string, contextDate: Date): number {
    const completion = this.completions.get(this.getContextKey(listId, contextDate));
    return completion ? completion.completedItemIds.length : 0;
  }

  // Migration helper: import old completion data
  migrateOldCompletionData(listId: string, items: { id: string; completed?: boolean }[], targetDate: Date = new Date()): void {
    const completedItemIds = items
      .filter(item => item.completed)
      .map(item => item.id);

    if (completedItemIds.length > 0) {
      const key = this.getContextKey(listId, targetDate);
      this.completions.set(key, {
        listId,
        contextDate: targetDate.toISOString().split('T')[0],
        completedItemIds,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      this.saveCompletions();
      console.log(`🔄 Migrated ${completedItemIds.length} completed items for list ${listId}`);
    }
  }

  // Invalidate cache for a specific list
  invalidateListCache(listId: string): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key => key.startsWith(`${listId}@`));
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      console.log(`🗑️ Universal Completion: Invalidated cache for ${key}`);
    });

    if (keysToDelete.length > 0) {
      console.log(`🔄 Universal Completion: Invalidated ${keysToDelete.length} cache entries for list ${listId}`);
    }
  }

  // Get raw completion data for backup
  getCompletionDataForBackup(): OptimizedCompletionStorage | null {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  // Restore completion data from backup
  restoreCompletionDataFromBackup(data: OptimizedCompletionStorage): void {
    if (data.v === 2) {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      this.loadV2Format(data);
      console.log('📋 Restored completion data from backup');
    } else {
      console.warn('Cannot restore: unsupported completion data version');
    }
  }

  // Debug method to log current state
  debugLogState(): void {
    console.log('🔍 Universal Completion Service State:', {
      totalCompletions: this.completions.size,
      cacheSize: this.cache.size,
      completionsByList: Array.from(this.completions.values()).reduce((acc, completion) => {
        acc[completion.listId] = (acc[completion.listId] || 0) + 1;
        return acc;
      }, {} as Record<string, number>)
    });
  }
}

// Export singleton instance
export const universalCompletionService = new UniversalCompletionService();
